import { Button, Divider, Form, Input, Modal, notification } from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { getOrganizations, updateOrganization } from 'services/organization';
import useOrganizationStore from 'store/useOrganizationStore';

interface OrgEditModalProps {
  orgId: string;
  isModalOpen: boolean;
  closeModal: () => void;
}

export const OrgEditModal: React.FC<OrgEditModalProps> = ({ orgId, isModalOpen, closeModal }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { organizations, updateOrganization: updateOrganizationStore } = useOrganizationStore();
  const [form] = Form.useForm();

  useEffect(() => {
    const selectedOrg = organizations.find((org) => org.id === orgId);
    if (!selectedOrg) return;

    const { displayName, phone, contact, address } = selectedOrg;
    form.setFieldsValue({ name: displayName, phone, contact, address });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const onFinish = async (values: any) => {
    // Reset name error
    form.setFields([{ name: 'name', errors: [] }]);

    // Validation for name input
    const regex = new RegExp(`^(?!org)(?=.[a-zA-Z0-9]).+$`);
    if (!regex.test(values.name)) {
      form.setFields([
        {
          name: 'name',
          errors: [`Name must contain letters or numbers, and not starts with 'org'`],
        },
      ]);
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        name: values.name,
        phone: values.phone,
        contact: values.contact,
        address: values.address,
      };
      await updateOrganization(orgId, data);
      const orgs = await getOrganizations();
      const updatedOrg = orgs.find((org) => org.id === orgId)!;
      updateOrganizationStore(orgId, { ...updatedOrg });
    } catch (err: any) {
      console.error(err);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
    closeModal();
  };

  return (
    <Modal title="Edit Setup" footer={null} visible={isModalOpen} onCancel={closeModal}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input showCount maxLength={30} placeholder="Organization name" />
        </Form.Item>
        <Form.Item
          label="Phone"
          name="phone"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Your organization phone number" />
        </Form.Item>
        <Form.Item
          label="Contact"
          name="contact"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Your organization contact info" />
        </Form.Item>
        <Form.Item
          label="Address"
          name="address"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Organization address" />
        </Form.Item>

        <Divider />

        <Form.Item wrapperCol={{ offset: 16, span: 24 }}>
          <Button onClick={closeModal} htmlType="button">
            Cancel
          </Button>
          <Button
            loading={isLoading}
            type="primary"
            htmlType="submit"
            style={{ marginLeft: '10px' }}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
